import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { HttpStatusCode } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CHECK_CLINIC_SHORT_NAME,
  DOWNLOAD_IMG,
  LOGIN,
} from "../../../enums/ApiPaths";
import { MASTER } from "../../../enums/Roles";
import * as UrlPath from "../../../enums/UrlPaths";
import * as Validation from "../../../features/auth/utils/AuthZodValidation.jsx";
import authApi from "../../../utils/api/authApi";
import * as Functions from "../../../utils/Functions";
import { useQueryClient } from "@tanstack/react-query";
import { SnackbarContext } from "../../../contexts/Snackbar.jsx";
import { LOGGED_IN_USER, LOGIN_KEYS } from "../../../enums/QueryKeys.jsx";
import { parseLoginData } from "../../../hooks/useUser.jsx";
import Buttons from "./component/Buttons.jsx";
import backgroundSvgDark from "../../../assets/svg/AuthBgLight.svg";
import zookeeperLogo from "../../../assets/svg/ZookeeperLogoWhite.svg";
import { ThemeContext } from "../../../contexts/ThemeContext.jsx";
import Loader from "../../../components/loader/Loader.jsx";
import { CALENDAR_VIEW } from "../../../enums/LocalStorageValues.jsx";
import { useLanguageAwareForm } from "../../../hooks/useLanguageAwareForm.jsx";
import ZookeeperMobileAdDialog from "../../../components/modals/bookingModalComponents/components/ZookeeperMobileAdDialog.jsx";
const ErrorMessage = ({ errorKey }) => {
  const { t } = useTranslation();
  return t(errorKey);
};

const useLoginResolver = () => {
  const { t } = useTranslation();
  const schema = Validation.useLogInResolver(t);
  return useMemo(() => {
    return zodResolver(schema);
  }, [schema]);
};

export default function LoginPage() {
  let { shortName } = useParams();
  const { setTheme } = React.useContext(ThemeContext);
  const [logo, setLogo] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const resolver = useLoginResolver();

  const methods = useLanguageAwareForm({
    defaultValues: Validation.logInDefaultValues,
    resolver: resolver,
    mode: "onSubmit",
  });

  const getFontSize = (text) => {
    const length = text.length;
    const calcSize = (max, min) => {
      const clampedLength = Math.min(length, 20);
      return max - ((clampedLength - 1) / (20 - 1)) * (max - min);
    };

    return {
      md: `${calcSize(160, 70)}px`,
      lg: `${calcSize(200, 80)}px`,
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = methods;

  const emailPattern = /\S+@\S+\.\S+/;
  const { handleSnackbar } = React.useContext(SnackbarContext);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Toggle password visibility
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    if (shortName) {
      checkIfShortNameIsValid();
      getClinicLogo();
    } else {
      setIsLoading(false);
    }

    if (Functions.isRefreshTokenValid()) {
      navigate(UrlPath.HOME);
    }

    setTheme("dark");
  }, []);

  async function checkIfShortNameIsValid() {
    try {
      let res = await authApi.get(CHECK_CLINIC_SHORT_NAME + shortName);
      if (res.status === HttpStatusCode.Ok) {
        if (!res.data) {
          navigate(UrlPath.LOGIN);
        }
      }
    } catch (error) {
      navigate(UrlPath.LOGIN);
    }
  }

  async function getClinicLogo() {
    try {
      let res = await authApi.get(DOWNLOAD_IMG + shortName);
      if (res.status === HttpStatusCode.Ok) {
        if (res.data) {
          setLogo(res.data);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  const loginSubmitHandler = async (e) => {
    setLoading(true);
    await login(e);
    setLoading(false);
  };

  // Login with credentials
  async function login(values) {
    const processedValues = processValues(values);
    const path = shortName ? LOGIN + "/" + shortName : LOGIN;
    try {
      let res = await authApi.post(path, processedValues);
      if (res.status === HttpStatusCode.Ok && res?.data !== "") {
        queryClient.setQueryData([LOGGED_IN_USER], res?.data);
        queryClient.setQueryData([LOGIN_KEYS], parseLoginData(res?.data));

        const loggedInUser = await queryClient.getQueryData([LOGGED_IN_USER]);
        //The login keys are only used here since the query is better at handling data than LS
        const loginKeys = await queryClient.getQueryData([LOGIN_KEYS]);

        Functions.setLocalStorageData(res?.data); //Setting token data
        setDefaultUserPreferences(res?.data);
        const lastUrl = Functions.getLastVisitedUrl();
        if (lastUrl) {
          Functions.removeLastUrl();
          navigate(lastUrl);
        } else {
          if (String(loggedInUser?.role) === MASTER) {
            navigate(UrlPath.ADMINISTRATION);
            return;
          } else if (Boolean(loginKeys?.needToChangePassword)) {
            navigate(UrlPath.CHANGE_FORGOTTEN_PASSWORD);
            return;
          } else if (Boolean(loginKeys?.needToAddPhoneNumber)) {
            Functions.navigateToPhoneSetup(navigate);
          } else {
            navigate(UrlPath.HOME);
          }
        }
      } else {
        setError("login", { type: "server", message: "SomethingWentWrong" });
        setError("password", { type: "server", message: "SomethingWentWrong" });
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const isInternalError =
        !error.response || (status >= 500 && status <= 599);
      const isHtmlResponse = typeof data === "string" && data.includes("<html");
      const errorKey =
        isInternalError || isHtmlResponse
          ? "ConnectionError"
          : data || "SomethingWentWrong";
      setError("login", { type: "server", message: errorKey });
      setError("password", { type: "server", message: errorKey });
    }
  }

  function setDefaultUserPreferences(data) {
    const defaultLanguage = data?.defaultLanguage
      ? data?.defaultLanguage
      : i18n.language;
    setTheme(data?.defaultTheme ?? "dark");
    i18n.changeLanguage(defaultLanguage);
    localStorage.setItem("lang", defaultLanguage);
    localStorage.setItem(CALENDAR_VIEW, data?.defaultCalendarView ?? "agenda");
  }

  function processValues(values) {
    return {
      ...(isEmail(values.login)
        ? { email: values.login }
        : { username: values.login }),
      password: values.password,
    };
  }

  function isEmail(email) {
    return emailPattern.test(email);
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${backgroundSvgDark})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* First Grid (Left Side) */}
      <Grid
        item
        flexDirection="column"
        justifyContent="space-between"
        display={{ xs: "none", md: "flex" }}
        md={7}
        lg={9}
      >
        <Grid
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          {shortName ? (
            <React.Fragment>
              <Typography
                variant="h1"
                textAlign="center"
                fontSize={getFontSize(String(shortName))}
                letterSpacing={4}
                fontWeight="bolder"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {String(shortName).toUpperCase()}
              </Typography>
              {logo ? (
                <img
                  src={logo}
                  width={"450px"}
                  style={{
                    marginBottom: "5px",
                  }}
                />
              ) : null}
            </React.Fragment>
          ) : null}
          <img src={zookeeperLogo} width={shortName ? "45%" : "70%"} />

          <Typography variant={shortName ? "h5" : "h3"} letterSpacing={8}>
            A new way to care
          </Typography>
          <Typography variant="body1" color="white" mt={5}>
            {t("PowerdByKilobyte")}
          </Typography>
        </Grid>
      </Grid>

      {/* Second Grid (Right Side) */}
      <Grid
        item
        xs={12}
        md={5}
        lg={3}
        sx={{
          bgcolor: "#001f3fbb",
          display: "flex",
          minHeight: "100vh",
          flexDirection: "column",
          justifyContent: "space-evenly",
          alignItems: "center",
          p: 5,
        }}
      >
        <Grid
          item
          display={{ xs: "flex", sm: "flex", md: "none" }}
          justifyContent={"center"}
          sx={{ width: "100%" }}
        >
          <img src={zookeeperLogo} width={"60%"} />
        </Grid>
        <Grid
          item
          display={{ xs: "none", sm: "none", md: "flex" }}
          justifyContent={"center"}
        >
          <Typography variant="h4">Prijava</Typography>
        </Grid>
        <Grid item width={"100%"}>
          <form onSubmit={handleSubmit(loginSubmitHandler)}>
            <Stack spacing={5}>
              {/* <LoginTitle /> */}
              <TextField
                fullWidth
                autoCapitalize="off"
                autoCorrect="off"
                variant="standard"
                label={shortName ? t("EmailOrUsername") : t("Email")}
                inputProps={{
                  maxLength: 120,
                  "aria-label": "Email or Username",
                  "data-testid": "auth.login.email-input",
                }}
                error={!!errors.login}
                helperText={
                  errors.login ? (
                    <ErrorMessage errorKey={errors.login.message} />
                  ) : null
                }
                {...register("login")}
              />
              <TextField
                fullWidth
                variant="standard"
                autoCapitalize="off"
                autoCorrect="off"
                inputProps={{
                  maxLength: 64,
                  "aria-label": "Password",
                  "data-testid": "auth.login.password-input",
                }}
                label={t("Password")}
                type={showPassword ? "text" : "password"}
                error={!!errors.password}
                helperText={
                  errors.password ? (
                    <ErrorMessage errorKey={errors.password.message} />
                  ) : null
                }
                {...register("password")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("TogglePasswordVisibility")}
                        title={t("TogglePasswordVisibility")}
                        data-testid="auth.login.password-toggle"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Stack alignItems={"flex-end"}>
                <Link
                  to={UrlPath.FORGOTTEN_PASSWORD}
                  data-testid="auth.login.forgot-password-link"
                >
                  {t("ForgottenPasswordLink")}
                </Link>
              </Stack>
              <Button
                type="submit"
                data-testid="auth.login.submit-button"
                variant="contained"
                color="secondary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {t("LogIn")}
              </Button>
              <Grid
                container
                spacing={2}
                justifyContent={"center"}
                display={"flex"}
              >
                <Grid item sx={6}>
                  <Typography>{t("DontHaveAnAccountQuestion")} </Typography>
                </Grid>
                <Grid item sx={6}>
                  <Link
                    to={UrlPath.SIGN_UP}
                    data-testid="auth.login.signup-link"
                  >
                    {t("SignUp")}
                  </Link>
                </Grid>
              </Grid>
            </Stack>
          </form>
        </Grid>
        <Grid item>
          <Buttons />
        </Grid>
      </Grid>
      <ZookeeperMobileAdDialog/>
    </Grid>
  );
}
